import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, SparkModalPortal } from '@spark';
import CostEstimateFooter from '@/features/billing/components/CostEstimateFooter';
import LibraryPickModal from '@/features/datasets/components/LibraryPickModal';
import { data_from_server_post } from '@/shared/http/client';
import { isGenerativeTask } from '@/shared/domain/taskTypes';
import { formatDate } from '@/shared/utils/helpers';
import {
    FIELD_LABELS,
    SCOPE_FULL,
    SCOPE_SPECIFIC_ONLY,
    SCOPE_TEACHER_ONLY,
    autoMapColumns,
    benchmarkFields,
    changeRows,
    mappingComplete,
    planBody,
    planTitle,
    primaryLabelFor,
    promptDiffCounts,
    resolveRevaluatePage,
    scopeCardBody,
} from '../utils/revaluateFlow';
import RevaluateSummaryView from './recalculate/RevaluateSummaryView';
import RevaluateSettingsView from './recalculate/RevaluateSettingsView';
import RevaluateChangesView, { RevaluateDiffView } from './recalculate/RevaluateChangesView';
import './RecalculateEvaluationParamsModal.css';

function readFileText(file) {
    if (typeof file?.text === 'function') return file.text();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(file);
    });
}

function countRecords(fileName, text, lines) {
    if (/\.jsonl$/i.test(fileName)) return lines.length;
    if (/\.json$/i.test(fileName)) {
        try {
            const parsed = JSON.parse(text);
            return Array.isArray(parsed) ? parsed.length : null;
        } catch {
            return null;
        }
    }
    return Math.max(lines.length - 1, 0);
}

async function inspectFile(file, validateFile) {
    if (validateFile) return validateFile(file);
    const text = await readFileText(file);
    const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '');
    const header = (lines[0] || '').replace(/^\uFEFF/, '');
    const columns = header.split(',').map((column) => column.trim()).filter(Boolean);
    if (!columns.length) return { ok: false, message: 'This file has no columns.' };
    return { ok: true, columns, records: countRecords(file.name || '', text, lines) };
}

function changeValue(changes, field, side) {
    return (changes || []).find((row) => row.field === field)?.[side];
}

function benchmarkRecordCounts(datasetsInfo) {
    const counts = {};
    (Array.isArray(datasetsInfo) ? datasetsInfo : []).forEach((info) => {
        if (!info?.is_benchmark || !info?.dataset) return;
        const records = info.request_example_summary_data?.num_records ?? info.total_records_in_file;
        if (records != null) counts[info.dataset] = records;
    });
    return counts;
}

/**
 * Re-evaluate dialog (Figma QxcawEtacqoJfH7R6F8Nu6 / 2:1144). Rendered through
 * `SparkModalPortal` so every view overlays the page instead of flowing inline
 * in the filter bar. Views: summary → settings (what to run + benchmarks),
 * summary → changes table → single prompt diff.
 */
export default function RecalculateEvaluationParamsModal({
    open,
    usecase,
    changeStatus = null,
    confirming = false,
    onCancel,
    onConfirm,
    uploadBenchmark,
    validateFile,
    initialDiffSide = null,
    runDateIso = null,
}) {
    const generative = isGenerativeTask(usecase?.task_type);
    const previousTeacher = changeValue(changeStatus?.changes, 'teacher_model', 'previous') || '';
    const currentTeacher = changeValue(changeStatus?.changes, 'teacher_model', 'current') || usecase?.teacher_model || '';
    const teacher = currentTeacher || 'the teacher';
    const benchmarksChanged = Boolean(changeStatus?.benchmarksChanged);
    const page = resolveRevaluatePage({
        teacherModelChanged: Boolean(changeStatus?.teacherModelChanged),
        teacherPromptChanged: Boolean(changeStatus?.teacherPromptChanged),
        specificPromptChanged: Boolean(changeStatus?.specificPromptChanged),
        benchmarksChanged,
        datasetsChanged: Boolean(changeStatus?.datasetsChanged),
        comparisonOn: Boolean(usecase?.comparison_mode),
        isGenerative: generative,
        previousTeacher,
        currentTeacher,
        benchmarkName: usecase?.benchmarks || [],
        versionLabel: changeStatus?.latestTrainingVersion != null
            ? `V${changeStatus.latestTrainingVersion}`
            : '',
    });

    const originalNames = useMemo(
        () => (usecase?.benchmarks || []).filter(Boolean),
        [usecase],
    );
    const fields = useMemo(
        () => benchmarkFields(usecase?.prompt),
        [usecase?.prompt],
    );

    const [view, setView] = useState('summary');
    const [scope, setScope] = useState(page.defaultScope);
    const [draftScope, setDraftScope] = useState(page.defaultScope);
    const [appliedNames, setAppliedNames] = useState(originalNames);
    const [draftNames, setDraftNames] = useState(originalNames);
    const [appliedFile, setAppliedFile] = useState(null);
    const [draftFile, setDraftFile] = useState(null);
    const [fileStatus, setFileStatus] = useState('idle');
    const [fileMessage, setFileMessage] = useState('');
    const [pendingName, setPendingName] = useState('');
    const [mapping, setMapping] = useState({});
    const [showMapping, setShowMapping] = useState(false);
    const [diffSide, setDiffSide] = useState(null);
    const [diffField, setDiffField] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [recordCounts, setRecordCounts] = useState({});
    const [libraryOpen, setLibraryOpen] = useState(false);
    const [libraryOptions, setLibraryOptions] = useState(null);
    const [libraryLoading, setLibraryLoading] = useState(false);
    const wasOpen = useRef(false);

    useEffect(() => {
        if (open && !wasOpen.current) {
            setView(initialDiffSide ? 'changes' : 'summary');
            setDiffSide(initialDiffSide);
            setDiffField(null);
            setScope(page.defaultScope);
            setDraftScope(page.defaultScope);
            setAppliedNames(originalNames);
            setDraftNames(originalNames);
            setAppliedFile(null);
            setDraftFile(null);
            setFileStatus('idle');
            setFileMessage('');
            setPendingName('');
            setMapping({});
            setShowMapping(false);
            setLibraryOpen(false);
            setLibraryOptions(null);
            setSubmitting(false);
        }
        wasOpen.current = Boolean(open);
    }, [open, initialDiffSide, originalNames, page.defaultScope]);

    // Record counts for the benchmark rows and the plan title (Figma shows "1,200 records").
    const clientId = usecase?.client_id;
    const usecaseId = usecase?._id;
    useEffect(() => {
        if (!open || !clientId || !usecaseId) return undefined;
        let cancelled = false;
        data_from_server_post(
            'datasets-and-benchmarks-meta-data',
            { client_id: clientId, llm_usecase_id: usecaseId },
            (result) => {
                if (!cancelled) setRecordCounts(benchmarkRecordCounts(result?.datasets_info));
            },
            () => {},
        );
        return () => {
            cancelled = true;
        };
    }, [open, clientId, usecaseId]);

    const openLibrary = () => {
        setLibraryOpen(true);
        if (libraryOptions || !clientId) return;
        setLibraryLoading(true);
        data_from_server_post(
            'datasets-and-benchmarks',
            { value: clientId },
            (result) => {
                const seen = new Set();
                const options = (result?.benchmarks || []).filter((item) => {
                    if (!item?.value || seen.has(item.value)) return false;
                    seen.add(item.value);
                    return true;
                }).map((item) => ({ ...item, label: item.value }));
                setLibraryOptions(options);
                setLibraryLoading(false);
            },
            () => {
                setLibraryOptions([]);
                setLibraryLoading(false);
            },
        );
    };

    const pageLocksTeacher = page.lockedScopes.includes(SCOPE_TEACHER_ONLY);
    const teacherLocked = pageLocksTeacher || Boolean(appliedFile?.ok);
    const draftTeacherLocked = pageLocksTeacher || Boolean(draftFile?.ok);
    const specificLocked = page.lockedScopes.includes(SCOPE_SPECIFIC_ONLY);

    useEffect(() => {
        if (teacherLocked && scope === SCOPE_TEACHER_ONLY) setScope(SCOPE_FULL);
    }, [teacherLocked, scope]);
    useEffect(() => {
        if (draftTeacherLocked && draftScope === SCOPE_TEACHER_ONLY) setDraftScope(SCOPE_FULL);
    }, [draftTeacherLocked, draftScope]);

    const toBenchmark = (name) => ({
        name,
        records: recordCounts[name],
        linked: originalNames.includes(name),
        updated: benchmarksChanged && originalNames.length === 1 && originalNames.includes(name),
    });
    const summaryBenchmarks = appliedNames.map(toBenchmark);
    if (appliedFile?.ok) {
        summaryBenchmarks.push({ name: appliedFile.file?.name || appliedFile.name, records: appliedFile.records });
    }
    const cards = [
        {
            id: SCOPE_FULL,
            title: 'Full evaluation',
            body: scopeCardBody(SCOPE_FULL, teacher),
            locked: false,
        },
        {
            id: SCOPE_TEACHER_ONLY,
            title: 'Teacher only',
            body: scopeCardBody(SCOPE_TEACHER_ONLY, teacher),
            locked: draftTeacherLocked,
        },
        generative ? {
            id: SCOPE_SPECIFIC_ONLY,
            title: 'Specific model only',
            body: scopeCardBody(SCOPE_SPECIFIC_ONLY, teacher),
            locked: specificLocked,
        } : null,
    ].filter(Boolean);
    // Figma scope notes: "Settings - Forced" (benchmark changed) and "Settings - Mapping *" (new file).
    let lockNote = null;
    if (benchmarksChanged) {
        lockNote = "Teacher only isn't available: the benchmark changed, so both models need to run on the updated records.";
    } else if (!pageLocksTeacher && draftFile?.ok) {
        lockNote = `Teacher only isn't available: ${draftFile.file?.name || draftFile.name} is new, so both models need to run on it.`;
    }

    let noteTitle = null;
    let noteText = null;
    if (draftScope !== SCOPE_SPECIFIC_ONLY && changeStatus?.teacherModelChanged) {
        noteTitle = 'Teacher responses';
        noteText = 'New teacher responses will be generated because the teacher model changed.';
    } else if (draftScope !== SCOPE_SPECIFIC_ONLY && changeStatus?.teacherPromptChanged) {
        noteTitle = 'Teacher responses';
        noteText = 'New teacher responses will be generated because the teacher prompt changed.';
    } else if (draftScope !== SCOPE_TEACHER_ONLY && generative && changeStatus?.specificPromptChanged) {
        noteTitle = 'Specific model responses';
        noteText = 'New Specific model responses will be generated because its prompt changed.';
    }

    const close = () => {
        setAppliedFile(null);
        setDraftFile(null);
        setView('summary');
        onCancel?.();
    };

    const clearDraftFile = () => {
        setDraftFile(null);
        setFileStatus('idle');
        setFileMessage('');
        setPendingName('');
        setMapping({});
        setShowMapping(false);
    };

    const onPickFile = async (file) => {
        if (!file) return;
        setFileStatus('validating');
        setFileMessage('');
        setPendingName(file.name);
        setDraftFile(null);
        try {
            const result = await inspectFile(file, validateFile);
            if (!result?.ok) {
                setFileStatus('error');
                setFileMessage(result?.message || 'This file cannot be used.');
                setDraftFile(null);
                setShowMapping(false);
                return;
            }
            const nextMapping = autoMapColumns(fields, result.columns);
            setDraftFile({
                ok: true,
                name: file.name.replace(/\.[^.]+$/, ''),
                file,
                columns: result.columns,
                records: result.records ?? null,
            });
            setMapping(nextMapping);
            setFileStatus('ready');
            setShowMapping(!mappingComplete(nextMapping, fields));
        } catch (error) {
            setFileStatus('error');
            setFileMessage(error?.message || 'This file cannot be used.');
            setDraftFile(null);
        }
    };

    const resetDraft = () => {
        setDraftScope(scope);
        setDraftNames(appliedNames);
        setDraftFile(appliedFile);
        setFileStatus(appliedFile?.ok ? 'ready' : 'idle');
        setFileMessage('');
        setPendingName('');
        setShowMapping(false);
    };

    const openSettings = () => {
        resetDraft();
        setView('settings');
    };

    const applySettings = () => {
        setScope(draftScope);
        setAppliedNames(draftNames);
        setAppliedFile(draftFile?.ok ? draftFile : null);
        setView('summary');
    };

    const discardSettings = () => {
        resetDraft();
        setView('summary');
    };

    const openChanges = (side = 'teacher') => {
        setDiffSide(side);
        setDiffField(null);
        setView('changes');
    };

    const run = async () => {
        let names = appliedNames.filter(Boolean);
        if (appliedFile?.ok) {
            if (uploadBenchmark) {
                setSubmitting(true);
                try {
                    const uploaded = await uploadBenchmark({
                        file: appliedFile.file,
                        name: appliedFile.name,
                        mapping,
                    });
                    names = [...names, uploaded || appliedFile.name];
                } catch (error) {
                    setSubmitting(false);
                    setFileMessage(error?.message || 'Upload failed.');
                    return;
                }
                setSubmitting(false);
            } else {
                names = [...names, appliedFile.name];
            }
        }
        await onConfirm?.({
            evaluationScope: scope,
            benchmarkNames: names,
        });
    };

    const rows = changeRows(changeStatus?.changes, diffSide === 'specific' ? 'specific' : 'teacher', {
        teacherModel: currentTeacher,
        teacherProvider: usecase?.teacher_provider || '',
    });
    const hasChanges = (changeStatus?.changes || []).length > 0;
    const diffRow = rows.find((row) => row.field === diffField);
    const applyDisabled = fileStatus === 'validating'
        || confirming
        || (showMapping && !mappingComplete(mapping, fields));

    const taskName = usecase?.name || usecase?.usecase_name || '';
    const lastRun = runDateIso ? formatDate(runDateIso) : '';
    const summarySubtitle = [taskName, lastRun ? `Last run ${lastRun}` : ''].filter(Boolean).join(' · ');

    let title = 'Re-evaluate';
    let subtitle = summarySubtitle;
    let primaryLabel = primaryLabelFor(scope);
    let onPrimary = run;
    let primaryDisabled = confirming || submitting;
    let showTertiary = true;
    let onTertiary = close;
    let onBack = undefined;
    let body = null;

    if (view === 'settings') {
        title = 'Evaluation settings';
        subtitle = 'Choose what to run and which benchmarks to use.';
        primaryLabel = 'Apply';
        onPrimary = applySettings;
        primaryDisabled = applyDisabled;
        onTertiary = discardSettings;
        body = (
            <RevaluateSettingsView
                cards={cards}
                recommendedScope={page.recommendedScope}
                scope={draftScope}
                onScopeChange={setDraftScope}
                lockNote={lockNote}
                benchmarks={draftNames.map(toBenchmark)}
                onToggleBenchmark={(name) => setDraftNames((current) => current.filter((item) => item !== name))}
                canAddBenchmarks={draftScope === SCOPE_FULL}
                onOpenLibrary={openLibrary}
                onPickFile={onPickFile}
                draftFile={draftFile}
                fileStatus={fileStatus}
                fileMessage={fileMessage}
                pendingName={pendingName}
                onRemoveFile={clearDraftFile}
                fields={fields}
                mapping={mapping}
                showMapping={showMapping}
                onMappingChange={(field, column) => setMapping((current) => ({ ...current, [field]: column }))}
                onEditMapping={() => setShowMapping(true)}
                onMappingDone={() => setShowMapping(false)}
                noteTitle={noteTitle}
                noteText={noteText}
            />
        );
    } else if (view === 'changes' && diffField && diffRow) {
        const { added, removed } = promptDiffCounts(diffRow.previous, diffRow.current);
        title = `${FIELD_LABELS[diffField] || 'Prompt'} changes`;
        subtitle = `${added} ${added === 1 ? 'line' : 'lines'} added · ${removed} removed`;
        showTertiary = false;
        onBack = () => setDiffField(null);
        body = <RevaluateDiffView previous={diffRow.previous} current={diffRow.current} />;
    } else if (view === 'changes') {
        const teacherSwitch = diffSide !== 'specific' && previousTeacher && currentTeacher && previousTeacher !== currentTeacher
            ? `${previousTeacher} → ${currentTeacher}`
            : '';
        title = diffSide === 'specific' ? 'Specific model changes' : 'Teacher changes';
        subtitle = [teacherSwitch, `${rows.length} ${rows.length === 1 ? 'setting' : 'settings'} changed`]
            .filter(Boolean)
            .join(' · ');
        showTertiary = false;
        onBack = () => setView('summary');
        body = <RevaluateChangesView rows={rows} onOpenDiff={setDiffField} />;
    } else {
        body = (
            <RevaluateSummaryView
                bannerText={page.summary}
                bannerSeverity={page.severity}
                hasChanges={hasChanges}
                planTitle={planTitle(scope, summaryBenchmarks)}
                planBody={planBody(scope, {
                    teacher,
                    benchmarkNames: summaryBenchmarks.map((benchmark) => benchmark.name),
                    benchmarkChanged: benchmarksChanged,
                })}
                onEdit={openSettings}
                onViewChanges={() => openChanges('teacher')}
            />
        );
    }

    // The quote follows the scope the user picks (teacher only / Specific only / full).
    const showEstimate = view === 'summary' && Boolean(usecase?._id);

    return (
        <>
            <SparkModalPortal open={open} onClose={close} size="slot">
                <Modal
                    open={open}
                    className="revaluate-modal"
                    title={title}
                    subtitle={subtitle}
                    showSubtitle={Boolean(subtitle)}
                    onBackClick={onBack}
                    fixedHeight={false}
                    primaryLabel={primaryLabel}
                    primaryLoading={submitting}
                    tertiaryLabel="Cancel"
                    showTertiaryButton={showTertiary}
                    showFooterSlot={showEstimate}
                    footerSlot={
                        showEstimate ? (
                            <CostEstimateFooter
                                operation="revaluate"
                                label="re-evaluation"
                                params={{ usecase_id: usecase._id, scope }}
                                enabled={open}
                            />
                        ) : null
                    }
                    primaryDisabled={primaryDisabled}
                    onPrimaryClick={onPrimary}
                    onTertiaryClick={onTertiary}
                    onCloseClick={close}
                >
                    {body}
                </Modal>
            </SparkModalPortal>
            <LibraryPickModal
                open={open && libraryOpen}
                kind="benchmark"
                loading={libraryLoading}
                options={(libraryOptions || []).filter((option) => !draftNames.includes(option.value))}
                onClose={() => setLibraryOpen(false)}
                onPick={(selected) => {
                    const picked = (selected || []).map((option) => option.value).filter(Boolean);
                    setDraftNames((current) => [...current, ...picked.filter((name) => !current.includes(name))]);
                    setLibraryOpen(false);
                }}
            />
        </>
    );
}
